/**
 * PORTED, WITH ATTRIBUTION, from elevenlabs/ui — `registry/elevenlabs-ui/ui/live-waveform.tsx`
 * (https://github.com/elevenlabs/ui), MIT licence, copyright ElevenLabs and the
 * elevenlabs/ui contributors. Every drawing number below is theirs. This is a
 * derivative work, not an original one.
 *
 * ---------------------------------------------------------------------------
 * THIS IS A DELIBERATE EXACT PORT, AND SOME OF UPSTREAM'S WEAKNESSES ARE
 * REPRODUCED ON PURPOSE.
 *
 * The requirement is that this widget have the typography, the look and the
 * animation of the ElevenLabs bar — not an improved version of them. An earlier
 * pass here "fixed" three things it judged weak and all three are gone again:
 *
 *   - THE AGENT'S TURN IS NO LONGER DRAWN DIFFERENTLY. Upstream has no
 *     agent-speaking state to port — its statuses are only
 *     disconnected/connecting/connected/error — so `listening` and `speaking`
 *     render IDENTICALLY here. The different direction of travel, the taller peaks
 *     and the higher alpha floor are deleted.
 *   - `thinking` IS UPSTREAM'S `processing`, unchanged. Same base (0.2), same
 *     amplitude, same speed as `connecting`. There is no calmer variant.
 *   - `error` DRAWS NOTHING SPECIAL. It is the idle waveform, exactly like
 *     `disconnected`. No colour change, no message; see `conversation-bar.tsx`.
 *
 * If a constant in here looks arbitrary, it is arbitrary upstream too, and
 * upstream is the specification.
 *
 * WHAT IS DRAWN FOR WHICH PHASE. `conversation-bar.tsx` owns the mapping and this
 * is the whole of it:
 *
 *   our phase                    upstream mode
 *   ---------------------------  ------------------------------------------------
 *   idle, ended, error, ending   idle — dotted 2 px centre line, canvas opacity-0
 *   connecting, thinking         `processing` — three summed sines, centre-weighted
 *   listening, speaking          `active` — upstream's `static` bars: a mirrored
 *                                FFT spectrum in fixed slots, redrawn in place
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A LITERAL COPY: THEIRS OPENS A MICROPHONE.
 *
 * Upstream's component is self-contained by design — it calls `getUserMedia`,
 * builds its own `AudioContext` and runs its own `AnalyserNode`, so you can drop
 * it on a page and it works. The `getUserMedia` is what is wrong here, not the
 * analyser. By the time this draws anything the microphone is ALREADY open and
 * already metered by `CallSession`; a second `getUserMedia` would prompt the
 * visitor a second time on some engines, cost a second capture track, and on
 * Safari a second `AudioContext` can decline to start at all while the first holds
 * the output. See the header of `lib/call/meter.ts`, which is the whole argument.
 *
 * So this component owns no audio graph at all. There is no `getUserMedia`, no
 * `AudioContext` and no `AnalyserNode` anywhere in this file, and there must never
 * be. What it gets instead are two read-only taps on the call's own graph, both
 * called inside its own animation frame and neither of them a prop that changes:
 *
 *   spectrum(track, out)  frequency bins, 0..255 — what upstream's `active` draws.
 *                         The meter runs a SECOND analyser per channel configured
 *                         with upstream's own settings, `fftSize = 256` and
 *                         `smoothingTimeConstant = 0.85`, so the numbers arriving
 *                         here are the numbers upstream's analyser would produce.
 *   levels()              one smoothed amplitude per direction, the fallback when
 *                         there is no spectrum to read.
 *
 * DO NOT SMOOTH THE LEVELS AGAIN. `AudioMeter.read` already applies an instant
 * attack and a ~180 ms release, and already maps RMS through a perceptual curve.
 * Adding a second time constant here is how a visualiser starts feeling late for
 * reasons nobody can find. The spectrum is deliberately NOT shaped that way: it is
 * raw bins at upstream's smoothing, because that is what upstream draws.
 *
 * ---------------------------------------------------------------------------
 * WHAT COULD NOT BE REPRODUCED EXACTLY, stated rather than hidden.
 *
 * 1. THE SPECTRUM IS THE PER-BIN MAXIMUM OF THE TWO DIRECTIONS, and this is the
 *    one place the picture is knowingly not upstream's. Upstream visualises the
 *    visitor's microphone and nothing else, so with echo cancellation doing its job
 *    their bar sits near-flat for the whole of the agent's turn — a dead waveform
 *    for half of every conversation. Two spectra are available here and no visual
 *    difference between the two directions is permitted, so each bin takes whichever
 *    channel is louder. Same algorithm, same constants, same mirror; one extra
 *    `max` per bin before it.
 * 2. THE 0.5 s FADE-OUT ON GOING IDLE, and the ~50 frame cross-fade from live bars
 *    into the processing swell, are both in upstream's source and both unreachable
 *    from upstream's own bar: it remounts the waveform across the
 *    connected/disconnected boundary with a `key`, and fades the whole canvas to
 *    `opacity-0` at the same time, so neither ramp is ever on screen. They are not
 *    implemented here, because the loop must stop when there is nothing to draw
 *    (below) and a fade-out is a reason to keep running.
 *
 * RESOLVED, AND WORTH KNOWING WHY. Until `meter.ts` grew `spectrum()` this file
 * could not draw upstream's `active` state at all: a mirrored spectrum needs a
 * spectrum, and all that was exposed was one scalar per direction. It rendered
 * upstream's OTHER code path instead — their `mode="scrolling"` branch, correct in
 * every constant but with bars that travelled where upstream's stand still. That
 * gap is closed. The scrolling branch survives below as the fallback for a call
 * with no `AudioContext`, which is the only case that now needs it.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS OURS AND INVISIBLE, which is the only kind of addition allowed.
 *
 * THE LOOP STOPS. Upstream's `requestAnimationFrame` runs for the lifetime of the
 * component, redrawing an idle canvas forever. This gets bundled into a widget
 * that runs on other people's websites, where a permanent 60 fps loop is a
 * battery complaint we would never hear about and could never reproduce. So the
 * loop runs only while there is motion to draw, and stops on unmount, on
 * `document.hidden`, in the `idle` mode, and whenever the visitor has asked for
 * reduced motion. Identical pixels, no idle cost.
 *
 * THE MEASUREMENTS ARE CACHED. Upstream calls `getBoundingClientRect` and
 * `getComputedStyle` inside every frame, both of which force layout or style; both
 * answers only change on a resize or a theme change, both of which re-run this
 * effect. No visual difference, real cost saved — and it is somebody else's page
 * paying it.
 *
 * THE BIN BUFFERS ARE ALLOCATED ONCE, per component rather than per frame, which
 * is why `spectrum()` takes a caller-supplied array. Three `Uint8Array`s for the
 * life of the widget instead of two per frame forever.
 *
 * ---------------------------------------------------------------------------
 * SHADOW DOM: THIS FILE NEEDS NO STYLESHEET AND NO TOKENS, as of 2026-08-24.
 *
 * It used to need two — `--muted-foreground` for the dotted idle line and an
 * inherited `color` for the bars — and inside the embed's shadow root neither is
 * declared unless `embed.css` happens to have loaded and re-declared it on `:host`.
 * A widget one failed stylesheet away from an invisible waveform is not a widget
 * that can be pasted onto a stranger's page, so both are gone: the dotted line is
 * `currentColor` at 0.2 (the same ink, from a property that always resolves) and
 * the bars take `barColor` from the caller, which `conversation-bar.tsx` now states
 * explicitly. `getComputedStyle(canvas).color` remains the FALLBACK, so a caller in
 * the panel can still let the cascade decide.
 *
 * There is no `className` prop any more for the same reason: a class is a request
 * for a stylesheet, and the two callers style their own container instead.
 */
import { useEffect, useRef } from 'react'

export interface LiveWaveformProps {
  /** Read in the animation frame. Never a changing prop. */
  levels: () => { user: number; assistant: number }
  /**
   * Frequency bins per direction, written into a buffer we own; returns how many
   * were written, or 0 when there is nothing to read. Optional, because a
   * hand-made `VoiceControl` in a test or a state preview has no FFT to offer —
   * see the fallback in the frame loop.
   */
  spectrum?: (track: 'user' | 'assistant', out: Uint8Array) => number
  /** Upstream's two booleans — `active` and `processing` — as the three states
   *  they actually encode. Nothing finer, because upstream draws nothing finer. */
  mode: 'idle' | 'processing' | 'active'
  height?: number
  barWidth?: number
  barGap?: number
  barRadius?: number
  barColor?: string
  /** Upstream's `sensitivity`: a linear gain on every bin before the clamp. Their
   *  own bar passes 1.8. */
  sensitivity?: number
  fadeEdges?: boolean
  fadeWidth?: number
}

/** Upstream's `historySize`. Sixty samples at one every 30 ms is 1.8 seconds. Only
 *  the fallback path keeps a history; the spectrum is redrawn in place. */
const HISTORY_SIZE = 60

/** Upstream's `updateRate`. How often the bars are recomputed. */
const SAMPLE_INTERVAL_MS = 30

/** Upstream's `barHeight`: the minimum drawn bar, in CSS pixels. A silent
 *  microphone must still draw a row of dots — an empty canvas is
 *  indistinguishable from a broken one. */
const BAR_HEIGHT_FLOOR = 4

/** Upstream clamps every value, measured or synthetic, to `Math.max(0.05, …)`. */
const VALUE_FLOOR = 0.05

/** Upstream's `value * rect.height * 0.8`. */
const AMPLITUDE = 0.8

/** Upstream's `ctx.globalAlpha = 0.4 + value * 0.6`. Quiet bars sit at the floor
 *  rather than vanishing, so the row keeps its shape through the gaps in speech. */
const ALPHA_FLOOR = 0.4
const ALPHA_SPAN = 0.6

/** Upstream's window on the spectrum: the bottom 5% of the bins are dropped as
 *  rumble and everything above 40% as hiss, leaving the band speech lives in. */
const BAND_START = 0.05
const BAND_END = 0.4

/**
 * How many bins we are prepared to receive. The meter runs a 256-point transform,
 * so a frame is 128 bins; the headroom means a future change there cannot silently
 * truncate the spectrum. The count actually used is the one `spectrum()` RETURNS,
 * never the buffer's length — upstream's band maths is a function of the real bin
 * count and would shift if it were fed the capacity instead.
 */
const BIN_CAPACITY = 256

/** Upstream's `processing` swell oscillates around this. The three waves sum to
 *  ±0.6, so at a base of 0.2 the swell spends part of every cycle clamped flat at
 *  the floor — it pulses, with pauses. Both `connecting` and `thinking` use it. */
const PROCESSING_BASE = 0.2

/** Upstream advanced their accumulator by 0.03 per FRAME, which at 60 fps is 1.8
 *  per second. Expressed per second here so the animation runs at upstream's speed
 *  on a 120 Hz display too, which is the same picture rather than a faster one. */
const PROCESSING_SPEED = 1.8

/**
 * Upstream's `processing` animation, static branch: three sines summed and weighted
 * towards the centre. Writes into `out` in place rather than allocating, because
 * this runs every frame for as long as the agent is thinking.
 *
 * The `Math.max(1, …)` on the half-count is the only change: upstream divides by
 * `Math.floor(barCount / 2)` and produces a canvas full of `NaN` if the container
 * is ever narrower than two bars. At a width of zero the loop below does not run,
 * so the guard is invisible.
 */
function swellBars(out: number[], count: number, t: number): void {
  out.length = 0
  const half = Math.max(1, Math.floor(count / 2))
  for (let i = 0; i < count; i++) {
    const p = (i - half) / half
    const centreWeight = 1 - Math.abs(p) * 0.4
    const w1 = Math.sin(t * 1.5 + p * 3) * 0.25
    const w2 = Math.sin(t * 0.8 - p * 2) * 0.2
    const w3 = Math.cos(t * 2 + p) * 0.15
    const v = (PROCESSING_BASE + w1 + w2 + w3) * centreWeight
    out.push(Math.max(VALUE_FLOOR, Math.min(1, v)))
  }
}

/**
 * Upstream's `active` bars, static branch, reproduced including its quirks.
 *
 * The band is taken out of the middle of the spectrum, and then the LEFT HALF IS
 * THE SAME DATA READ BACKWARDS: bar 0 is the top of the band, the two centre bars
 * are both the bottom of it, and the right half climbs back out. That is what
 * makes the waveform symmetrical about the centre and puts the loud low
 * frequencies of speech in the middle of the pill.
 *
 * Two upstream quirks kept deliberately. An ODD bar count produces `count - 1`
 * bars, so the rightmost slot stays empty — `paint` leaves it empty rather than
 * inventing a value. And the band index is `floor((i / half) * span)`, which
 * repeats bins when there are more bars than band, exactly as theirs does.
 */
function spectrumBars(
  out: number[],
  bins: Uint8Array,
  binCount: number,
  count: number,
  sensitivity: number,
): void {
  out.length = 0
  const half = Math.floor(count / 2)
  if (half < 1) return
  const start = Math.floor(binCount * BAND_START)
  const span = Math.floor(binCount * BAND_END) - start
  if (span < 1) return
  const bar = (i: number): number => {
    const idx = start + Math.floor((i / half) * span)
    const v = Math.min(1, ((bins[idx] ?? 0) / 255) * sensitivity)
    return Math.max(VALUE_FLOOR, v)
  }
  // Mirror the data for symmetric display: descending for the left half, then
  // ascending for the right. Upstream's comment and upstream's two loops.
  for (let i = half - 1; i >= 0; i--) out.push(bar(i))
  for (let i = 0; i < half; i++) out.push(bar(i))
}

/** Upstream's three `aria-label` strings, kept word for word. The bar's own live
 *  region says which phase the call is in; this only describes the picture. */
const ARIA: Record<LiveWaveformProps['mode'], string> = {
  idle: 'Audio waveform idle',
  processing: 'Processing audio',
  active: 'Live audio waveform',
}

export function LiveWaveform({
  levels,
  spectrum,
  mode,
  height = 64,
  barWidth = 3,
  barGap = 1,
  barRadius = 1.5,
  barColor,
  sensitivity = 1,
  fadeEdges = true,
  fadeWidth = 24,
}: LiveWaveformProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  // Upstream keeps `staticBarsRef` and `historyRef` apart and so do we: the swell,
  // the spectrum and the fallback history are three different pictures, and a
  // detour through `thinking` must not wipe the one `listening` comes back to.
  const staticRef = useRef<number[]>([])
  const historyRef = useRef<number[]>([])
  const swellRef = useRef<number[]>([])

  /** Allocated once per component, never per frame — the reason `spectrum()` takes
   *  a caller-supplied array. `merged` holds the per-bin maximum of the two. */
  const binsRef = useRef<{ user: Uint8Array; assistant: Uint8Array; merged: Uint8Array } | null>(null)
  binsRef.current ??= {
    user: new Uint8Array(BIN_CAPACITY),
    assistant: new Uint8Array(BIN_CAPACITY),
    merged: new Uint8Array(BIN_CAPACITY),
  }

  /**
   * `levels` and `spectrum` are documented as never changing, and this makes that a
   * courtesy rather than a requirement: a caller who rebuilds the closures every
   * render gets fresh ones without restarting the loop and losing the picture.
   */
  const levelsRef = useRef(levels)
  levelsRef.current = levels
  const spectrumRef = useRef(spectrum)
  spectrumRef.current = spectrum

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const step = barWidth + barGap
    const bins = binsRef.current!

    // Measured once per resize and then read from these, rather than inside the
    // frame as upstream does. See the header.
    let w = 0
    let h = 0
    let fade: CanvasGradient | null = null
    let colour: string | null = null

    const measure = (): void => {
      const rect = container.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      w = rect.width
      h = rect.height
      canvas.width = Math.max(1, Math.round(w * dpr))
      canvas.height = Math.max(1, Math.round(h * dpr))
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      // `setTransform`, not `scale`. Assigning `canvas.width` resets the transform
      // today, so upstream's `scale(dpr, dpr)` happens to be correct — but it is
      // correct by accident, and compounds the moment that stops being true.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      fade = null
      colour = null
    }

    /**
     * Upstream's inherited bar colour, resolved once per effect run instead of once
     * per frame. `getComputedStyle(canvas).color` means the canvas obeys `text-*`
     * classes like any glyph would, so it themes for free in light mode, dark mode
     * and inside a host page whose tokens we have never seen.
     */
    const resolveColour = (): string => {
      if (barColor) return barColor
      if (colour !== null) return colour
      colour = getComputedStyle(canvas).color || '#000'
      return colour
    }

    /**
     * `indexed` draws `bars[i]` at slot `i`, left to right — upstream's static
     * branch, which is what the spectrum and the processing swell both use.
     * Otherwise the newest sample is drawn at the right edge and the history trails
     * away to the left, which is upstream's scrolling branch and is only reached by
     * the no-spectrum fallback.
     */
    const paint = (bars: readonly number[], indexed: boolean): void => {
      if (w <= 0 || h <= 0) return
      ctx.clearRect(0, 0, w, h)

      const count = Math.floor(w / step)
      const centreY = h / 2
      ctx.fillStyle = resolveColour()

      for (let j = 0; j < count; j++) {
        const v = indexed ? bars[j] : bars[bars.length - 1 - j]
        // Upstream's loop stops at the shorter of the bar count and the data; this
        // is the same thing said per slot. It is what leaves an odd count's last
        // slot empty, and what lets the fallback waveform fill in from its edge.
        if (v === undefined) continue
        const barH = Math.max(BAR_HEIGHT_FLOOR, v * h * AMPLITUDE)
        const x = indexed ? j * step : w - (j + 1) * step
        ctx.globalAlpha = ALPHA_FLOOR + v * ALPHA_SPAN
        if (barRadius > 0) {
          ctx.beginPath()
          ctx.roundRect(x, centreY - barH / 2, barWidth, barH, barRadius)
          ctx.fill()
        } else {
          ctx.fillRect(x, centreY - barH / 2, barWidth, barH)
        }
      }
      ctx.globalAlpha = 1

      // Upstream's edge fade, and it is a good trick: a cached horizontal gradient
      // that is opaque at the ends and clear through the middle, filled under
      // `destination-out`, which removes what it covers. A CSS mask would need a
      // compositing layer per instance and would not survive being nested inside a
      // host page's transforms. This is one `fillRect`.
      if (fadeEdges && fadeWidth > 0) {
        if (!fade) {
          const g = ctx.createLinearGradient(0, 0, w, 0)
          const fadePercent = Math.min(0.3, fadeWidth / w)
          g.addColorStop(0, 'rgba(255,255,255,1)')
          g.addColorStop(fadePercent, 'rgba(255,255,255,0)')
          g.addColorStop(1 - fadePercent, 'rgba(255,255,255,0)')
          g.addColorStop(1, 'rgba(255,255,255,1)')
          fade = g
        }
        ctx.globalCompositeOperation = 'destination-out'
        ctx.fillStyle = fade
        ctx.fillRect(0, 0, w, h)
        ctx.globalCompositeOperation = 'source-over'
      }
    }

    const clear = (): void => {
      if (w > 0 && h > 0) ctx.clearRect(0, 0, w, h)
    }

    /**
     * Both spectra into their own buffers, then the per-bin maximum into a third.
     * Returns the bin count, or 0 when there is nothing to read.
     *
     * The two `n` values are honoured separately rather than assumed equal because
     * a channel with nothing attached returns 0 having written nothing — and its
     * buffer still holds the LAST frame it did write. Reading that would freeze one
     * direction's spectrum at whatever it said when the track went away.
     *
     * See note 1 in the header: taking the louder of the two is the one deliberate
     * divergence from upstream, which draws the microphone alone.
     */
    const readSpectrum = (): number => {
      const read = spectrumRef.current
      if (!read) return 0
      const nUser = read('user', bins.user)
      const nAssistant = read('assistant', bins.assistant)
      const n = Math.max(nUser, nAssistant)
      if (n === 0) return 0
      for (let i = 0; i < n; i++) {
        const u = i < nUser ? bins.user[i]! : 0
        const a = i < nAssistant ? bins.assistant[i]! : 0
        bins.merged[i] = u > a ? u : a
      }
      return n
    }

    // Read once per effect run, which means a visitor who changes the OS setting
    // mid-call is honoured at the next phase change rather than instantly. A
    // permanent media-query listener on every instance of an embedded widget is
    // not worth that difference.
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const animated = mode !== 'idle' && !reduced

    if (mode === 'idle') {
      // A finished call must not leave last turn's bars behind for the next one.
      staticRef.current.length = 0
      historyRef.current.length = 0
      swellRef.current.length = 0
    }

    /** One held frame, for reduced motion. The processing swell frozen at t=0 is a
     *  shape that reads as a waveform without moving; an empty canvas does not. */
    const still = (): void => {
      if (mode === 'idle') {
        clear()
        return
      }
      swellBars(swellRef.current, Math.floor(w / step), 0)
      paint(swellRef.current, true)
    }

    let raf = 0
    let running = false
    let startedAtMs = 0
    let lastSampleMs = 0
    let dirty = true
    /**
     * Which `active` picture is being drawn. Latched once the spectrum has been
     * read successfully, so a call cannot visibly change rendering style halfway
     * through: after that a zero means the track has been detached and `idle` is
     * milliseconds away, and holding the last frame is better than a flat line.
     *
     * False for the whole of a call with no `AudioContext` — the fallback recorder
     * path, where `spectrum` returns 0 forever — and for a hand-made `VoiceControl`
     * that has no `spectrum` at all. Both then get the level-driven scroll.
     */
    let hasSpectrum = false

    const frame = (nowMs: number): void => {
      if (mode === 'processing') {
        // The swell is a function of time, so every frame is a new shape and there
        // is nothing to skip.
        const t = ((nowMs - startedAtMs) / 1000) * PROCESSING_SPEED
        swellBars(swellRef.current, Math.floor(w / step), t)
        paint(swellRef.current, true)
      } else {
        if (nowMs - lastSampleMs >= SAMPLE_INTERVAL_MS) {
          lastSampleMs = nowMs
          const n = readSpectrum()
          if (n > 0) {
            hasSpectrum = true
            spectrumBars(staticRef.current, bins.merged, n, Math.floor(w / step), sensitivity)
          } else if (!hasSpectrum) {
            const l = levelsRef.current()
            const hist = historyRef.current
            hist.push(Math.min(1, Math.max(VALUE_FLOOR, Math.max(l.user, l.assistant))))
            if (hist.length > HISTORY_SIZE) hist.shift()
          }
          dirty = true
        }
        // Upstream's `needsRedraw`, and it matters more here than there. The bars
        // only change when a sample lands, which is every 30 ms — so on a 60 Hz
        // display half the frames would redraw an identical picture, and on a
        // 120 Hz one three quarters would. The frame loop still has to run at
        // display rate to stay in step with the sample clock; the drawing does not.
        if (dirty) {
          if (hasSpectrum) paint(staticRef.current, true)
          else paint(historyRef.current, false)
          dirty = false
        }
      }
      raf = requestAnimationFrame(frame)
    }

    const startLoop = (): void => {
      if (running) return
      running = true
      startedAtMs = performance.now()
      lastSampleMs = 0
      // The backing store may have been resized, or this may be a resume after the
      // tab was hidden. Either way the first frame must actually draw.
      dirty = true
      raf = requestAnimationFrame(frame)
    }

    const stopLoop = (): void => {
      if (!running) return
      running = false
      cancelAnimationFrame(raf)
    }

    // A background tab still services `requestAnimationFrame` on some engines, and
    // the audio behind it is stale anyway.
    const onVisibility = (): void => {
      if (document.hidden) stopLoop()
      else startLoop()
    }

    measure()
    const observer = new ResizeObserver(() => {
      measure()
      // A resize resets the backing store, which wipes the canvas — so the next
      // frame must draw even if no new sample has landed, and a held frame has to
      // be re-held by hand because there is no next frame coming.
      dirty = true
      if (!animated) still()
    })
    observer.observe(container)

    if (animated) {
      document.addEventListener('visibilitychange', onVisibility)
      if (!document.hidden) startLoop()
    } else {
      still()
    }

    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      stopLoop()
    }
  }, [mode, barWidth, barGap, barRadius, barColor, sensitivity, fadeEdges, fadeWidth])

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', boxSizing: 'border-box', width: '100%', height: `${height}px` }}
      role="img"
      aria-label={ARIA[mode]}
    >
      {mode === 'idle' && (
        // Upstream's idle state, kept as-is. A dotted rule says "nothing is being
        // measured" without implying silence, which a flat solid line would.
        //
        // `currentColor` at 0.2, not `--muted-foreground/20`: identical ink, and it
        // is what makes this file render correctly with no stylesheet — see the
        // stylesheet note in the header.
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            borderTop: '2px dotted currentColor',
            opacity: 0.2,
          }}
        />
      )}
      <canvas
        ref={canvasRef}
        // `display: block` matters: a canvas is inline by default and would sit on
        // a text baseline, leaving a few pixels of gap under it.
        style={{ display: 'block', width: '100%', height: '100%' }}
        aria-hidden="true"
      />
    </div>
  )
}
